import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import TopAppBar from '../../components/feature/TopAppBar';
import Button from '../../components/base/Button';
import AddressCard from '../../components/cards/AddressCard';
import StatusBadge from '../../components/base/StatusBadge';

export default function JobDetail() {
  const navigate = useNavigate();
  const { orderId } = useParams();
  const [showAcceptModal, setShowAcceptModal] = useState(false);
  
  const jobDetails = {
    orderId: orderId || 'DOM2847',
    branchName: 'Domino\'s Stockholm City',
    status: 'ready' as const,
    pickupAddress: 'Drottninggatan 89, 111 60 Stockholm',
    pickupContact: 'Store Manager',
    pickupPhone: '+46 8 123 456',
    deliveryAddress: 'Sveavägen 45, 3tr, 113 59 Stockholm',
    customerName: 'Erik Andersson',
    customerPhone: '+46 70 123 4567',
    customerNote: 'Ring på dörren, porten är trasig. Lämna inte vid dörren.',
    items: [
      '1x Pepperoni Passion Large',
      '1x Margherita Medium',
      '2x Coca-Cola 50cl',
      '1x Vitlöksbröd',
    ],
    allergens: 'Gluten, Laktos',
    distance: '2.4 km',
    eta: '9 min',
    payout: '89',
    paymentType: 'Kort (betald online)',
  };
  
  const handleAccept = () => {
    setShowAcceptModal(false);
    navigate('/active-delivery');
  };
  
  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      <TopAppBar 
        title="Orderdetaljer" 
        showBack 
        onBack={() => navigate('/jobs')}
      />
      
      <div className="pt-14">
        <div className="relative h-64 bg-gray-200">
          <iframe
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2034.8!2d18.0686!3d59.3293!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNTnCsDE5JzQ1LjUiTiAxOMKwMDQnMDcuMCJF!5e0!3m2!1sen!2sse!4v1234567890"
            className="w-full h-full"
            style={{ border: 0 }}
            loading="lazy"
          ></iframe>
          <div className="absolute top-4 left-4 right-4 flex items-center justify-between">
            <div className="bg-white px-3 py-2 rounded-full shadow-lg">
              <p className="text-sm font-bold text-gray-900">{jobDetails.distance} • {jobDetails.eta}</p>
            </div>
            <StatusBadge status={jobDetails.status} text="Redo" />
          </div>
        </div>
        
        <div className="px-4 py-4 space-y-4">
          <div className="bg-white rounded-2xl p-4 shadow-md">
            <div className="flex items-start justify-between mb-3">
              <div>
                <p className="text-xs font-mono font-bold text-gray-500 mb-1">ORDER #{jobDetails.orderId}</p>
                <p className="text-base font-bold text-gray-900">{jobDetails.branchName}</p>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-[#E31837]">{jobDetails.payout} SEK</p>
                <p className="text-xs text-gray-600">{jobDetails.paymentType}</p>
              </div>
            </div>
          </div>
          
          <AddressCard
            type="pickup"
            address={jobDetails.pickupAddress}
            contactName={jobDetails.pickupContact}
            contactPhone={jobDetails.pickupPhone}
            onCall={() => window.open(`tel:${jobDetails.pickupPhone}`)}
            onNavigate={() => window.open(`https://maps.google.com/?q=${jobDetails.pickupAddress}`)}
            onCopy={() => navigator.clipboard.writeText(jobDetails.pickupAddress)}
          />
          
          <AddressCard
            type="dropoff"
            address={jobDetails.deliveryAddress}
            contactName={jobDetails.customerName}
            contactPhone={jobDetails.customerPhone}
            note={jobDetails.customerNote}
            onCall={() => window.open(`tel:${jobDetails.customerPhone}`)}
            onNavigate={() => window.open(`https://maps.google.com/?q=${jobDetails.deliveryAddress}`)}
            onCopy={() => navigator.clipboard.writeText(jobDetails.deliveryAddress)}
          />
          
          <div className="bg-white rounded-2xl p-4 shadow-md">
            <h3 className="text-sm font-bold text-gray-900 mb-3">Orderinnehåll</h3>
            <div className="space-y-2">
              {jobDetails.items.map((item, index) => (
                <div key={index} className="flex items-center gap-2">
                  <i className="ri-checkbox-circle-line text-green-600"></i>
                  <span className="text-sm text-gray-700">{item}</span>
                </div>
              ))}
            </div>
            
            {jobDetails.allergens && (
              <div className="mt-3 pt-3 border-t border-gray-100">
                <div className="flex items-start gap-2 p-2 bg-red-50 rounded-lg">
                  <i className="ri-alert-line text-red-600 mt-0.5"></i>
                  <div>
                    <p className="text-xs font-bold text-red-900">Allergener</p>
                    <p className="text-xs text-red-800">{jobDetails.allergens}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 shadow-lg">
        <Button
          onClick={() => setShowAcceptModal(true)}
          variant="primary"
          size="large"
          fullWidth
          icon={<i className="ri-check-line"></i>}
        >
          Acceptera order
        </Button>
      </div>
      
      {showAcceptModal && (
        <div className="fixed inset-0 bg-black/50 flex items-end z-50">
          <div className="bg-white rounded-t-3xl w-full p-6 animate-slide-up">
            <div className="w-12 h-1 bg-gray-300 rounded-full mx-auto mb-6"></div>
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-check-line text-3xl text-green-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Bekräfta acceptans</h3>
              <p className="text-sm text-gray-600">
                Du kommer att börja navigera till upphämtningsplatsen
              </p>
            </div>
            <div className="space-y-3">
              <Button onClick={handleAccept} variant="primary" size="large" fullWidth>
                Starta leverans
              </Button>
              <Button onClick={() => setShowAcceptModal(false)} variant="tertiary" size="large" fullWidth>
                Avbryt
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
